import React, { useState, useEffect, useContext } from "react";
import CommunicationGrid from "../Components/CommunicationGrid";
import { ToastContext } from "../utils/ToastContext";
import { useSelector } from "react-redux";
import { handleDateFormat } from "../utils/Common";
import Loader from "../Components/Loader";
import MUISearchBar from "../Components/MUISearchBar";
import { Box } from "@mui/material";
import DashboardDropdown from "../Components/DashboardDropdown";
import GridPairDatePicker from "../Components/GridPairDatePickerv2";
import TimelineSelector from "../Components/TimelineSelector";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import LeadDataModal from "../Components/LeadDataModal";
import CommunicationTemplateFormDrawer from "../Components/CommunicationTemplateFormDrawer";
import ActionMenu from "../Components/ActionMenu";
import { getSMSTemplatesList } from "../Services/templatesService/SMSTemplateService";
//
import {
  getLeadDistributionRatio,
  updateLeadDistributionRatio,
} from "../Services/leadDistributionService/leadDistributionService";
import UpdateLeadDistributionModal from "../Components/UpdateLeadDistributionModal";
dayjs.extend(isoWeek);

function LeadDistributionRatio() {
  const userDetails = useSelector(
    (state) => state.user.userDetails || { UserDetails: {} },
  );

  const [modalVisibility, setModalVisibility] = useState({
    datePicker: false,
    addTemplateDrawer: false,
  });

  const [tableState, setTableState] = useState({
    tableColumns: [],
    tableData: [],
    totalRecords: 0,
    pageNumber: 1,
    recordsPerPage: 25,
  });

  const thisWeekStart = dayjs().startOf("isoWeek").format("YYYY-MM-DD");
  const today = dayjs().format("YYYY-MM-DD");

  const [filterState, setFilterState] = useState({
    dateRange: { fromDate: "", toDate: "" },
    customDateRange: { fromDate: "", toDate: "" },
    searchQuery: "",
  });

  const [rawData, setRawData] = useState([]);
  const [manualTrigger, setManualTrigger] = useState(0);
  const [remountKey, setRemountKey] = useState(0);

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleCheckedRows = (ids) => {
    setSelectedRowIds(ids);
  };

  useEffect(() => {
    document.title = "Lead Distribution Ratio";
  }, []);

  const [loader, setLoader] = useState(false);
  const { showToast } = useContext(ToastContext);

  const initialColumns = useSelector(
    (state) => state.leadsColumn.selectedColumns,
  );

  const initialSelectedColumns = new Set(initialColumns);
  const [selectedColumns, setSelectedColumns] = useState(
    initialSelectedColumns,
  );

  const updateModalVisibility = (modalName, isVisible) => {
    setModalVisibility((prev) => ({ ...prev, [modalName]: isVisible }));
  };

  const updateTableState = (key, value) => {
    setTableState((prev) => ({ ...prev, [key]: value }));
  };

  const updateFilterState = (key, value) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
    handlePageReset();
  };

  // Fetch communication template data
  const getLeadDistributionData = async () => {
    setLoader(true);
    try {
      const data = await getLeadDistributionRatio(
        {
          currentPage: parseInt(tableState.pageNumber),
          recordsPerPage: tableState.recordsPerPage,
          searchQuery: filterState.searchQuery,
        },
        filterState.dateRange.fromDate,
        filterState.dateRange.toDate,
      );

      if (
        data &&
        data.data.count > 0 &&
        Array.isArray(data?.data?.leadDistributionRatio)
      ) {
        const ratioColumns = [
          { key: "name", header: "Team Name" },
          { key: "maxLimit", header: "Max Limit" },
          { key: "ratio", header: "Ratio" },
          { key: "count", header: "Count" },
          { key: "lastModifiedBy", header: "Last Modified By" },
          { key: "lastModifiedAt", header: "Last Modified At" },
        ];

        updateTableState("totalRecords", data.data.count);
        updateTableState("tableColumns", ratioColumns);

        setSelectedColumns(new Set(ratioColumns.map((col) => col.header)));

        setRawData(data.data.leadDistributionRatio);

        updateTableState(
          "tableData",
          generateTableData(data.data.leadDistributionRatio),
        );
      } else {
        const ratioColumns = [
          { key: "name", header: "Team Name" },
          { key: "maxLimit", header: "Max Limit" },
          { key: "ratio", header: "Ratio" },
          { key: "count", header: "Count" },
        ];

        updateTableState("totalRecords", 0);
        updateTableState("tableColumns", ratioColumns);
        updateTableState("tableData", []);
      }
    } catch (error) {
      console.error(error);
      showToast("Something Went Wrong", "error");
    }
    setLoader(false);
  };

  useEffect(() => {
    getLeadDistributionData();
  }, [
    filterState.dateRange,
    tableState.pageNumber,
    tableState.recordsPerPage,
    manualTrigger,
    filterState.searchQuery,
  ]);

  const handlePageRefresh = () => {
    setManualTrigger((prev) => prev + 1);
  };

  const generateTableData = (data) => {
    return (
      data?.map((item) => ({
        id: item.id,
        name: item.name,
        maxLimit: item.maxLimit,
        ratio: item.ratio,
        count: item.count,
        lastModifiedBy: item.lastModifiedBy ?? "",
        lastModifiedAt: item.lastModifiedAt
          ? handleDateFormat(item.lastModifiedAt)
          : "",
        leadsjson: item,
      })) || []
    );
  };

  // Filter table columns based on user selection
  const filteredTableColumns = tableState.tableColumns.filter((column) =>
    selectedColumns.has(column.header),
  );

  // Filter table data based on selected columns
  const filteredTableData = tableState.tableData.map((row) => {
    const filteredRow = {
      id: row.id,
      leadsjson: row.leadsjson || row.leadsJson || row,
    };
    Object.keys(row).forEach((key) => {
      const columnName = tableState.tableColumns.find(
        (col) => col.key === key,
      )?.header;
      if (selectedColumns.has(columnName)) {
        filteredRow[key] = row[key];
      }
    });
    return filteredRow;
  });

  const handlePageChange = (currentPage, recordsPerPage) => {
    updateTableState("pageNumber", parseInt(currentPage));
    updateTableState("recordsPerPage", recordsPerPage);
  };

  const handlePageReset = () => {
    updateTableState("pageNumber", parseInt(1));
  };

  const handleClearFilter = () => {
    updateModalVisibility("datePicker", false);
    handlePageReset();
    setRemountKey((prevKey) => prevKey + 1);
    setFilterState({
      dateRange: { fromDate: "", toDate: "" },
      searchQuery: "",
    });
    localStorage.removeItem("searchCriteria");
    localStorage.removeItem("savedAdvanceSearch");
    localStorage.removeItem("queryConnectorLeads");
    window.dispatchEvent(new CustomEvent("searchCriteriaUpdated"));
  };

  const handleCustomDateChange = (dateRange) => {
    updateFilterState("dateRange", {
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    });
  };

  const handleActionMenuItemSelect = (selectedItem) => {
    switch (selectedItem) {
      case "Add Template":
        updateModalVisibility("addTemplateDrawer", true);
        break;
      default:
        showToast("select correct option", "warning");
    }
  };

  const handleActionSelect = (action) => {
    if (action === "Update Ratio") {
      const row = tableState.tableData.find(
        (item) => item.id === selectedRowIds[0],
      );

      setSelectedRow(row);
      setOpenUpdateModal(true);
    }
  };

  const handleUpdateRatio = async (payload) => {
    try {
      setLoader(true);

      const response = await updateLeadDistributionRatio(payload);

      showToast(
        response?.message || "Lead Distribution Ratio updated successfully",
        "success",
      );

      setOpenUpdateModal(false);
      setSelectedRow(null);

      await getLeadDistributionData();
    } catch (error) {
      console.error(error);

      showToast(
        error?.response?.data?.error?.message ||
          "Failed to update Lead Distribution Ratio",
        "error",
      );
    } finally {
      setLoader(false);
    }
  };

  const handleCloseUpdateModal = () => {
    setOpenUpdateModal(false);
    setSelectedRow(null);
  };

  return (
    <div style={styles.Container}>
      {loader === true ? <Loader /> : null}

      <div style={styles.mainContent}>
        <div style={styles.headerBar}>
          <div style={styles.headerText}>
            <p>Lead Distribution Ratio</p>
          </div>
        </div>

        <CommunicationTemplateFormDrawer
          open={modalVisibility.addTemplateDrawer}
          onClose={() => updateModalVisibility("addTemplateDrawer", false)}
          onSuccess={getLeadDistributionData}
        />

        <Box
          sx={(theme) => ({
            display: "flex",
            backgroundColor: "#F1F4FA",
            borderRadius: "4px",
            gap: "10px",
            justifyContent: "flex-start",
            padding: "30px 0px 10px 0px",
            alignItems: "center",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "column",
              lg: "column",
              xl: "row",
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              backgroundColor: "#F1F4FA",
              marginRight: "20px",
              justifyContent: "center",
            }}
          >
            <UpdateLeadDistributionModal
              open={openUpdateModal}
              selectedRow={selectedRow}
              onClose={handleCloseUpdateModal}
              onSubmit={handleUpdateRatio}
            />
          </Box>
        </Box>

        <div>
          <CommunicationGrid
            isLoading={loader}
            tableName="LeadDistributionRatio"
            columns={filteredTableColumns}
            tabledata={filteredTableData}
            clearAllFilter={handleClearFilter}
            totalRecords={tableState.totalRecords}
            onPageChange={handlePageChange}
            handleRefresh={handlePageRefresh}
            checkedRowIds={handleCheckedRows}
            actionSelect={handleActionSelect}
            showActionButton={true}
          />
        </div>
      </div>
    </div>
  );
}

export default LeadDistributionRatio;

const styles = {
  Container: {
    display: "flex",
    height: "100vh",
    backgroundColor: "#F1F4FA",
  },
  mainContent: {
    flex: 1,
    overflowX: "auto",
    overflowY: "auto",
  },
  headerBar: {
    height: "30px",
    paddingTop: "30px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    margin: 0,
    color: "#06152B",
    fontSize: "25px",
    fontWeight: "600",
    flex: "1",
  },
  headerIcon: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    gap: "35px",
    flexWrap: "wrap",
    paddingRight: "30px",
  },
};
